"use client";

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListMessage } from '@/types/messages';
import { List, X, Check } from 'lucide-react';

interface ListMessagePreviewProps {
  message: ListMessage;
}

export function ListMessagePreview({ message }: ListMessagePreviewProps) {
  const { t } = useTranslation();
  const { title, description, footer, buttonText, sections } = message;
  const [showListModal, setShowListModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  return (
    <div className="relative w-full h-full">
      {/* WhatsApp-style background */}
      <div 
        className="absolute inset-0 rounded-lg overflow-hidden"
        style={{
          backgroundColor: '#f5f1eb'
        }}
      >
        {/* Background pattern with opacity */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'url("https://storage.googleapis.com/stark-media/wa-bg-2.png")',
            backgroundRepeat: 'repeat',
            backgroundSize: 'cover'
          }}
        ></div>
      </div>

      {/* Content container */}
      <div className="relative z-10 p-4 h-full min-h-[400px]">
        {/* Message bubble */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-[280px] ml-auto relative">
          {/* Message tail */}
          <div className="absolute -right-2 top-3 w-0 h-0 border-l-8 border-l-white border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>

            {/* Message Content */}
            <div className="p-4 pb-2">
              {title && (
                <h3 className="font-medium text-gray-900 text-base whitespace-pre-wrap">{title}</h3>
              )}
              
              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap mt-1">{description}</p>

              {footer && (
                <p className="text-gray-500 text-sm whitespace-pre-wrap mt-1">{footer}</p>
              )}
              
              {/* Timestamp */}
              <div className="text-right mt-1">
                <span className="text-xs text-gray-400">
                  {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </span>
              </div>
            </div>

            {/* List Button */}
            <div className="border-t">
              <div 
                className="flex items-center justify-center p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => setShowListModal(true)}
              >
                <List className="w-5 h-5 mr-3 text-green-600" />
                <span className="text-green-600 font-medium text-sm">{buttonText || 'view options'}</span>
              </div>
            </div>
          </div>

        {/* List Modal - positioned within the preview container */}
        {showListModal && (
          <div className="absolute inset-0 bg-black bg-opacity-20 z-50 flex items-end justify-center rounded-lg overflow-hidden">
            <div className="bg-white w-3/4 rounded-t-2xl shadow-xl max-h-[80%] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-3 border-b flex-shrink-0">
                <h3 className="font-medium text-base">{buttonText || 'View Products'}</h3>
                <button 
                  onClick={() => setShowListModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="overflow-y-auto flex-1">
                {sections.map((section, sectionIndex) => (
                  <div key={sectionIndex}>
                    <div className="px-3 py-2 bg-gray-100 border-b sticky top-0">
                      <h4 className="font-semibold text-xs uppercase tracking-wide text-gray-600">{section.name}</h4>
                    </div>
                    {section.options.map((option, optionIndex) => (
                      <div 
                        key={optionIndex} 
                        className={`px-3 py-3 border-b hover:bg-gray-50 cursor-pointer flex items-center justify-between ${
                          selectedItem === option.id ? 'bg-green-50' : ''
                        }`}
                        onClick={() => setSelectedItem(option.id)}
                      >
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">{option.title}</div>
                          {option.description && (
                            <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                          )}
                        </div>
                        {selectedItem === option.id && (
                          <Check className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="p-3 border-t flex-shrink-0">
                <div className="text-center text-xs text-gray-500 mb-2">
                  {t('preview.tapToSelect')}
                </div>
                <button 
                  className="w-full bg-green-600 text-white py-2 rounded-lg font-medium text-sm hover:bg-green-700 disabled:opacity-50"
                  disabled={!selectedItem}
                  onClick={() => {
                    setShowListModal(false);
                    setSelectedItem(null);
                  }}
                >
                  {t('preview.send')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}